import React from 'react';
import { FaCode, FaLaptopCode, FaUsers, FaEnvelope, FaMobileAlt, FaCloud, FaPaintBrush, FaServer, FaCodeBranch, FaUsersCog } from 'react-icons/fa';
import { useSpring, animated } from '@react-spring/web';
import './About.css';

const AboutSection = ({ title, content, image }) => {
  const scrollAnimation = useSpring({
    opacity: 1,
    transform: 'translateY(0)',
    from: { opacity: 0, transform: 'translateY(50px)' },
    reset: true,
    delay: 200,
  });

  return (
    <animated.section className="about-section-unique" style={scrollAnimation}>
      <div className="about-content-unique">
        <h1 className="about-title-unique"><FaCode /> {title}</h1>
        {content.map((paragraph, index) => (
          <p key={index} className="about-description-unique">{paragraph}</p>
        ))}
      </div>
      {image && (
        <div className="about-image-unique">
          <img src={image.src} alt={image.alt} className="about-image-img-unique" />
        </div>
      )}
    </animated.section>
  );
};

const ServicesSection = ({ services }) => {
  const scrollAnimation = useSpring({
    opacity: 1,
    transform: 'translateY(0)',
    from: { opacity: 0, transform: 'translateY(50px)' },
    reset: true,
    delay: 300,
  });

  return (
    <animated.section className="services-section-unique" style={scrollAnimation}>
      <h2 className="services-title-unique"><FaLaptopCode /> Our Services</h2>
      <ul className="services-list-unique">
        {services.map((service, index) => (
          <li key={index} className="services-item-unique">
            <i className={service.icon}></i> {service.text}
          </li>
        ))}
      </ul>
    </animated.section>
  );
};

const ExperienceSection = ({ experiences }) => {
  const scrollAnimation = useSpring({
    opacity: 1,
    transform: 'translateY(0)',
    from: { opacity: 0, transform: 'translateY(50px)' },
    reset: true,
    delay: 400,
  });

  return (
    <animated.section className="experience-section-unique" style={scrollAnimation}>
      <h2 className="experience-title-unique"><FaLaptopCode /> Our Expertise</h2>
      <ul className="experience-list-unique">
        {experiences.map(({ title, description }, index) => (
          <li key={index} className="experience-item-unique">
            <strong>{title}</strong>
            <p className="experience-description-unique">{description}</p>
          </li>
        ))}
      </ul>
    </animated.section>
  );
};

const TestimonialsSection = ({ testimonials }) => {
  const scrollAnimation = useSpring({
    opacity: 1,
    transform: 'translateY(0)',
    from: { opacity: 0, transform: 'translateY(50px)' },
    reset: true,
    delay: 500,
  });

  return (
    <animated.section className="testimonials-section-unique" style={scrollAnimation}>
      <h2 className="testimonials-title-unique"><FaUsers /> Client Testimonials</h2>
      <ul className="testimonials-list-unique">
        {testimonials.map((testimonial, index) => (
          <li key={index} className="testimonials-item-unique">
            &quot;{testimonial.quote}&quot; - {testimonial.author}
          </li>
        ))}
      </ul>
    </animated.section>
  );
};

const ContactSection = ({ email, message }) => {
  const scrollAnimation = useSpring({
    opacity: 1,
    transform: 'translateY(0)',
    from: { opacity: 0, transform: 'translateY(50px)' },
    reset: true,
    delay: 600,
  });

  return (
    <animated.section className="contact-section-unique" style={scrollAnimation}>
      <h2 className="contact-title-unique"><FaEnvelope /> Contact Us</h2>
      <p className="contact-description-unique">{message}</p>
      <a href={`mailto:${email}`} className="contact-button-unique"><FaEnvelope /> Get in Touch</a>
    </animated.section>
  );
};

const About = () => {
  const aboutContent = [
    "Welcome to CodeProwl Technologies, a leading software development company specializing in creating innovative and scalable solutions for businesses worldwide.",
    "We offer end-to-end software development services, including web development, mobile app development, desktop software, backend development, database management, and software optimization.",
    "Our team of experienced developers is dedicated to delivering high-quality, secure, and user-friendly solutions tailored to your business needs.",
    "We leverage the latest technologies and industry best practices to ensure your software is robust, scalable, and future-proof.",
    "Whether you need a new application, want to optimize existing software, or improve security, CodeProwl Technologies is here to help."
  ];

  const services = [
    { icon: <FaCode />, text: "Custom Web Development" },
    { icon: <FaMobileAlt />, text: "Mobile App Development (iOS & Android)" },
    { icon: <FaLaptopCode />, text: "Desktop Software Development" },
    { icon: <FaServer />, text: "Backend Development & API Integration" },
    { icon: <FaCloud />, text: "Cloud Solutions & Deployment" },
    { icon: <FaPaintBrush />, text: "UI/UX Design & Optimization" },
    { icon: <FaCodeBranch />, text: "Software Maintenance & Optimization" },
    { icon: <FaUsersCog />, text: "Security Enhancements & Audits" }
  ];

  const expertise = [
    { title: "Web Development", description: "We build responsive, high-performance websites using modern frameworks like React, Angular, and Node.js." },
    { title: "Mobile App Development", description: "We create cross-platform and native mobile apps using Flutter, React Native, and Kotlin." },
    { title: "Backend Development", description: "We develop scalable and secure backend systems with technologies like Django, Spring Boot, and Express.js." },
    { title: "Database Management", description: "We design and optimize databases using MySQL, PostgreSQL, MongoDB, and Firebase." },
    { title: "Software Optimization", description: "We improve the performance and efficiency of your existing software." },
    { title: "Security Solutions", description: "We provide security audits, vulnerability assessments, and implement robust security measures." }
  ];

  const testimonials = [
    { quote: "CodeProwl Technologies delivered a seamless web application that exceeded our expectations.", author: "Happy Client" },
    { quote: "Their team is highly skilled and professional. They transformed our mobile app idea into reality.", author: "Satisfied Customer" },
    { quote: "The backend system they developed is robust and scalable. Highly recommended!", author: "Tech Startup CEO" }
  ];

  const contactInfo = {
    email: "contact@codeprowl.com",
    message: "Let’s build something amazing together! Reach out to us for a consultation or project discussion."
  };

  return (
    <div className="about-container-unique">
      <AboutSection 
        title="About CodeProwl Technologies" 
        content={aboutContent} 
        image={{ src: "/images/banner/12.png", alt: "CodeProwl Technologies Team" }} 
      />
      <ServicesSection services={services} />
      <ExperienceSection experiences={expertise} />
      <TestimonialsSection testimonials={testimonials} />
      <ContactSection {...contactInfo} />
    </div>
  );
};

export default About;